import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft } from "react-icons/fa";
import styles from "./PurchasePage.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPurchaseDetail,
  getCurrentPurchase,
  getCurrentPurchaseLoading,
  getPartyOptions,
  clearCurrentPurchase,
  fetchPurchasePartiesOptions,
} from "../../features/purchase/purchaseSlice";
import { toast } from "react-toastify";
import { toastStyles } from "../../static/styles";
import { PUT } from "../../services/axios.service";
import { IPurchaseFormInput } from "../../types/purchase";
import PurchaseForm from "../../components/purchase/PurchaseForm";

const PurchaseEditPage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const currentPurchase = useAppSelector(getCurrentPurchase);
  const currentPurchaseLoading = useAppSelector(getCurrentPurchaseLoading);
  const partyOptions = useAppSelector(getPartyOptions);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchPurchaseDetail(Number(id)));
      dispatch(fetchPurchasePartiesOptions());
    }
    return () => {
      dispatch(clearCurrentPurchase());
    };
  }, [id, dispatch]);

  const handleGoBack = () => {
    navigate(route.purchase);
  };

  const handleSubmit = async (data: IPurchaseFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/purchases/${id}`, {
        party_id: data.party_id,
        date: data.date,
        voucher_no: data.voucher_no,
        discount: data.discount,
        tax: data.tax,
        items: data.items,
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.purchase);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo(() => {
    if (!currentPurchase) return undefined;
    return {
      party_id: currentPurchase.party_id,
      date: currentPurchase.date,
      voucher_no: currentPurchase.voucher_no || "",
      discount: currentPurchase.discount,
      tax: currentPurchase.tax,
      items:
        currentPurchase.details && currentPurchase.details.length > 0
          ? currentPurchase.details.map((d) => ({
              item_id: d.item_id,
              quantity: parseFloat(String(d.quantity)),
              rate: d.rate,
            }))
          : [{ item_id: 0, quantity: 0, rate: 0 }],
    };
  }, [currentPurchase]);

  const defaultPartyOption = useMemo(() => {
    if (!currentPurchase) return null;
    return (
      partyOptions.find((p) => p.value === currentPurchase.party_id) || null
    );
  }, [currentPurchase, partyOptions]);

  return (
    <Layout>
      <div className={styles.PurchasePage}>
        <div className="logoSection">
          <h1
            onClick={handleGoBack}
            className="text-4xl font-bold text-white lg:text-2xl cursor-pointer lg:w-fit"
          >
            {companyName}
          </h1>
          <hr className="w-[180px] border-white border-[4px] rounded-[4px] mx-auto mt-4 lg:mx-0 lg:w-24 lg:border-[2px] lg:mt-2" />
        </div>

        <div className="pageTitleSection">
          <FaArrowLeft
            onClick={handleGoBack}
            className="lg:hidden absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />
          <h1 className="text-5xl mt-6 lg:mt-0 font-bold text-white lg:text-2xl lg:text-primary-1">
            ખરીદી ફેરફાર કરો
          </h1>
        </div>

        <div className="mt-[60px] px-10 py-5">
          <div className="lg:bg-white lg:shadow lg:rounded-md lg:p-6 mt-8">
            {currentPurchaseLoading ? (
              <p className="text-primary-2 font-bold text-xl text-center py-4">
                રાહ જુઓ...
              </p>
            ) : currentPurchase && defaultValues ? (
              <PurchaseForm
                key={currentPurchase.id}
                defaultValues={defaultValues}
                defaultPartyOption={defaultPartyOption}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                onCancel={handleGoBack}
              />
            ) : (
              <p className="text-primary-2 font-bold text-xl text-center py-4">
                ખરીદી ની વિગતો મળી નથી
              </p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PurchaseEditPage;
